import React, { useEffect, useState } from "react";
import {
  ALPHANUMERIC_REGEX,
  EMAIL_REGEX,
  NON_SPECIAL_CHAR_REGEX,
} from "../../../../helpers/constants";
import { useSelector } from "react-redux";
import { Checkbox, Input, Select } from "../../../../components/ds";

// Optional structured incident-response function. Mirrors the backend CrrUsers.IncidentRole
// choices. Independent of the free-text Department field above.
export const INCIDENT_ROLE_OPTIONS = [
  { value: "", label: "— None —" },
  { value: "first_call", label: "First call" },
  { value: "it_msp", label: "IT / MSP" },
  { value: "incident_commander", label: "Incident commander" },
  { value: "finance_ap", label: "Finance / AP" },
  { value: "otrp_policy", label: "OTRP / policy" },
  { value: "legal_privacy", label: "Legal / privacy" },
  { value: "legal_procurement", label: "Legal / procurement" },
  { value: "affected_system_data_owner", label: "Data owner" },
];

const FormFields = ({
  isEdit,
  values,
  errors,
  onChange,
  onBlurField,
}) => {
  const [domainOptions, setDomainOptions] = useState([]);
  const { newEmployeeError, domainsList } = useSelector(
    (state) => state.userManagement
  );

  useEffect(() => {
    if (domainsList && domainsList.length > 0) {
      const options = domainsList.map((domain) => ({
        id: domain.id,
        option: domain.name,
      }));
      setDomainOptions(options);
    }
  }, [domainsList]);

  const fieldError = (name) =>
    errors?.[name] ||
    (newEmployeeError && typeof newEmployeeError === "object"
      ? newEmployeeError[name]
      : null);

  const selectedDomains = values.domains_restricted_to || [];

  const toggleDomain = (id, checked) => {
    const next = checked
      ? [...selectedDomains, id]
      : selectedDomains.filter((d) => d !== id);
    onChange("domains_restricted_to", next);
  };

  return (
    <div className="ops-form-stack">
      <div className="ops-field">
        <label className="ops-field-label" htmlFor="emp-name">
          Employee Name
        </label>
        <Input
          id="emp-name"
          name="name"
          value={values.name || ""}
          disabled={isEdit}
          placeholder="Employee Name"
          invalid={Boolean(fieldError("name"))}
          onChange={(e) => onChange("name", e.target.value)}
          onBlur={() => onBlurField("name")}
          autoComplete="off"
        />
        {fieldError("name") ? (
          <p className="ops-field-error">{fieldError("name")}</p>
        ) : null}
      </div>

      <div className="ops-form-row">
        <div className="ops-field">
          <label className="ops-field-label" htmlFor="emp-id">
            Employee ID
          </label>
          <Input
            id="emp-id"
            name="employee_id"
            value={values.employee_id || ""}
            disabled={isEdit}
            placeholder="Employee ID"
            invalid={Boolean(fieldError("employee_id"))}
            onChange={(e) => onChange("employee_id", e.target.value)}
            onBlur={() => onBlurField("employee_id")}
            autoComplete="off"
          />
          {fieldError("employee_id") ? (
            <p className="ops-field-error">{fieldError("employee_id")}</p>
          ) : null}
        </div>
        <div className="ops-field">
          <label className="ops-field-label" htmlFor="emp-department">
            Department
          </label>
          <Input
            id="emp-department"
            name="department"
            className="grid-field"
            value={values.department || ""}
            placeholder="Department"
            invalid={Boolean(fieldError("department"))}
            onChange={(e) => onChange("department", e.target.value)}
            onBlur={() => onBlurField("department")}
            autoComplete="off"
          />
          {fieldError("department") ? (
            <p className="ops-field-error">{fieldError("department")}</p>
          ) : null}
        </div>
      </div>

      <div className="ops-field">
        <label className="ops-field-label" htmlFor="emp-incident-role">
          Incident role <span className="ops-field-optional">(optional)</span>
        </label>
        <Select
          id="emp-incident-role"
          value={values.incident_role || ""}
          options={INCIDENT_ROLE_OPTIONS}
          onChange={(v) => onChange("incident_role", v)}
        />
      </div>

      {domainOptions.length > 0 ? (
        <div className="ops-field">
          <span className="ops-field-label">Domains Assigned</span>
          <div className="ops-domain-checks" role="group" aria-label="Domains">
            {domainOptions.map((domain) => (
              <Checkbox
                key={domain.id}
                checked={selectedDomains.includes(domain.id)}
                onChange={(e) => toggleDomain(domain.id, e.target.checked)}
              >
                {domain.option}
              </Checkbox>
            ))}
          </div>
          {fieldError("domains_restricted_to") ? (
            <p className="ops-field-error">
              {fieldError("domains_restricted_to")}
            </p>
          ) : null}
        </div>
      ) : null}

      <div className="ops-field">
        <label className="ops-field-label" htmlFor="emp-email">
          Email ID
        </label>
        <Input
          id="emp-email"
          name="email"
          type="email"
          value={values.email || ""}
          disabled={isEdit}
          placeholder="Email ID"
          invalid={Boolean(fieldError("email"))}
          onChange={(e) => onChange("email", e.target.value)}
          onBlur={() => onBlurField("email")}
          autoComplete="off"
        />
        {fieldError("email") ? (
          <p className="ops-field-error">{fieldError("email")}</p>
        ) : null}
      </div>
    </div>
  );
};

export function validateEmployeeFields(values, isEdit) {
  const errors = {};
  if (!isEdit) {
    if (!values.name?.trim()) {
      errors.name = "Please fill the employee name to proceed!";
    } else if (!NON_SPECIAL_CHAR_REGEX.test(values.name)) {
      errors.name = "Please enter a valid name!";
    }
    if (!values.employee_id?.trim()) {
      errors.employee_id = "Please fill the employee id to proceed!";
    } else if (!ALPHANUMERIC_REGEX.test(values.employee_id)) {
      errors.employee_id = "Please enter a valid Employee Id to proceed";
    }
    if (!values.email?.trim()) {
      errors.email = "Please fill the email to proceed!";
    } else if (!EMAIL_REGEX.test(values.email)) {
      errors.email = "Invalid Email ID";
    }
  }
  if (!values.department?.trim()) {
    errors.department = "Please fill the department to proceed!";
  }
  return errors;
}

export default FormFields;
